import React from 'react';
import './BurgerSafetyBlog.css';

const BurgerSafetyBlog: React.FC = () => {
    return (
        <div className="blog-content">
            <div className="blog-header">
                <img src="/files/Logo1.png" alt="DeliFeed Logo" className="blog-logo" />
                <h1>How to Cook Burgers Safely at Home</h1>
                <p className="blog-meta">Essential food safety guide for perfect burgers every time</p>
            </div>

            <div className="blog-section">
                <h2>Why Burger Safety Matters</h2>
                <p>
                    Cooking burgers safely is more challenging than preparing regular cuts of meat, and understanding why is crucial for your health. When bacteria contaminate a whole piece of meat like a steak, they remain on the surface. This means you only need to cook the outside thoroughly to kill any harmful bacteria, which is why you can safely enjoy a rare steak.
                </p>
                <p>
                    <strong>Minced meat is different.</strong> During the grinding process, bacteria from the surface gets mixed throughout the entire patty. This means bacteria can be present in the center of your burger, not just on the outside. That's why burgers must be cooked all the way through to ensure safety.
                </p>
            </div>

            <div className="blog-section highlight-box">
                <h2>Safe Internal Temperatures</h2>
                <p>To ensure your burger is safe to eat, the <strong>coolest part</strong> of the patty must reach one of these time-temperature combinations:</p>

                <div className="temperature-table">
                    <div className="temp-row">
                        <div className="temp-value">70°C (158°F)</div>
                        <div className="temp-time">for 2 minutes</div>
                    </div>
                    <div className="temp-row">
                        <div className="temp-value">75°C (167°F)</div>
                        <div className="temp-time">for 30 seconds</div>
                    </div>
                    <div className="temp-row">
                        <div className="temp-value">80°C (176°F)</div>
                        <div className="temp-time">for 6 seconds</div>
                    </div>
                </div>

                <div className="tip-box">
                    <strong>💡 Pro Tip:</strong> Use a digital probe thermometer (also called a needle thermometer) to measure the internal temperature accurately. Insert it into the center of the patty, which is typically the coolest part.
                </div>
            </div>

            <div className="blog-section">
                <h2>Cross-Contamination Prevention</h2>
                <p>
                    Preventing cross-contamination is just as important as cooking to the right temperature. Follow these essential rules:
                </p>

                <div className="safety-rules">
                    <div className="rule-item">
                        <span className="rule-icon">🔪</span>
                        <div className="rule-content">
                            <h3>Separate Surfaces</h3>
                            <p>Never let cooked food touch surfaces that have been in contact with raw meat. Use separate cutting boards and plates for raw and cooked meat.</p>
                        </div>
                    </div>

                    <div className="rule-item">
                        <span className="rule-icon">🧼</span>
                        <div className="rule-content">
                            <h3>Proper Disinfection</h3>
                            <p>Clean and disinfect all surfaces, utensils, and equipment that touched raw meat before using them for cooked food. Use hot, soapy water followed by a food-safe sanitizer.</p>
                        </div>
                    </div>

                    <div className="rule-item">
                        <span className="rule-icon">🍴</span>
                        <div className="rule-content">
                            <h3>Fresh Utensils</h3>
                            <p>Use clean tongs or spatulas for cooked burgers. Never use the same utensils you used for raw meat without washing them first.</p>
                        </div>
                    </div>

                    <div className="rule-item">
                        <span className="rule-icon">❄️</span>
                        <div className="rule-content">
                            <h3>Storage Safety</h3>
                            <p>Keep raw meat refrigerated until cooking time. Never leave raw or cooked meat at room temperature for more than 2 hours (1 hour if it's above 32°C/90°F).</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="blog-section">
                <h2>Quick Safety Checklist</h2>
                <ul className="checklist">
                    <li>✓ Use a probe thermometer to check internal temperature</li>
                    <li>✓ Cook until center reaches at least 70°C for 2 minutes</li>
                    <li>✓ Use separate cutting boards for raw and cooked meat</li>
                    <li>✓ Wash hands thoroughly after handling raw meat</li>
                    <li>✓ Clean all surfaces that touched raw meat immediately</li>
                    <li>✓ Never reuse marinades from raw meat without boiling first</li>
                    <li>✓ Store leftover burgers in the refrigerator within 2 hours</li>
                </ul>
            </div>

            <div className="blog-footer-note">
                <p>
                    <strong>Remember:</strong> Following these guidelines doesn't just make your burgers safer—it protects you and your loved ones from foodborne illness. When in doubt, cook it through!
                </p>
            </div>
        </div>
    );
};

export default BurgerSafetyBlog;
